import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isCircuitOpen, trackQueryResult, onCircuitChange } from "@/lib/circuit-breaker";
import { getGlobalSingleton } from "@/lib/global-singleton";

// ─── Singleton Workspace Store (globalThis — sobrevive ao HMR do Vite) ───

type WorkspaceListener = () => void;

export interface WorkspaceMemberInfo {
  memberId: string | null;
  memberRole: string | null;
  memberDisplayName: string | null;
  memberPermissions: Record<string, unknown> | null;
}

interface WorkspaceState extends WorkspaceMemberInfo {
  workspaceId: string | null;
  workspaceName: string;
  workspaceMetadata: Record<string, unknown> | null;
  loading: boolean;
  /** Incremented on invalidateWorkspaceCache(); forces refetch in useWorkspace effect */
  invalidationCounter: number;
}

const initialMember: WorkspaceMemberInfo = {
  memberId: null,
  memberRole: null,
  memberDisplayName: null,
  memberPermissions: null,
};

interface WorkspaceRuntime {
  store: WorkspaceState;
  lastFetchedUserId: string | null;
  lastToastFn: ((t: any) => void) | null;
  pendingRefetch: boolean;
  isFetching: boolean;
  retryCount: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  bgRetryTimer: ReturnType<typeof setInterval> | null;
  listeners: Set<WorkspaceListener>;
}

function wsRuntime(): WorkspaceRuntime {
  return getGlobalSingleton<WorkspaceRuntime>("__fabchatWorkspaceRuntime__", () => ({
    store: {
      workspaceId: null,
      workspaceName: "",
      workspaceMetadata: null,
      loading: true,
      ...initialMember,
      invalidationCounter: 0,
    },
    lastFetchedUserId: null,
    lastToastFn: null,
    pendingRefetch: false,
    isFetching: false,
    retryCount: 0,
    retryTimer: null,
    bgRetryTimer: null,
    listeners: new Set<WorkspaceListener>(),
  }));
}

const MAX_RETRIES = 3;

// Re-fetch automaticamente quando o circuit breaker fechar (sai do modo degradado).
onCircuitChange((newState) => {
  const rt = wsRuntime();
  if (newState === "CLOSED" && rt.pendingRefetch && rt.lastFetchedUserId && rt.lastToastFn) {
    rt.pendingRefetch = false;
    rt.retryCount = 0;
    fetchWorkspace(rt.lastFetchedUserId, rt.lastToastFn);
  }
});

function notifyWsListeners() {
  wsRuntime().listeners.forEach((fn) => fn());
}

function setStore(partial: Partial<WorkspaceState>) {
  Object.assign(wsRuntime().store, partial);
  notifyWsListeners();
}

function isTransientError(error: any): boolean {
  const msg = error?.message || error?.details || "";
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("timeout") ||
    msg.includes("schema cache") ||
    msg.includes("PGRST002") ||
    msg.includes("connection") ||
    msg.includes("503") || msg.includes("504") || msg.includes("502") ||
    msg.includes("57014") ||
    msg.includes("upstream request timeout") ||
    error?.code === "PGRST002" ||
    error?.code === "57014"
  );
}

function startBackgroundRetry(userId: string, showToast: (t: any) => void) {
  const rt = wsRuntime();
  if (rt.bgRetryTimer) return;
  console.log("[useWorkspace] Starting background retry every 120s");
  rt.bgRetryTimer = setInterval(() => {
    rt.retryCount = 0;
    fetchWorkspace(userId, showToast);
  }, 120000);
}

function stopBackgroundRetry() {
  const rt = wsRuntime();
  if (rt.bgRetryTimer) {
    clearInterval(rt.bgRetryTimer);
    rt.bgRetryTimer = null;
  }
}

async function fetchWorkspace(userId: string, showToast: (t: any) => void) {
  const rt = wsRuntime();
  if (rt.isFetching) return;

  if (isCircuitOpen()) {
    console.log("[useWorkspace] Circuit breaker OPEN, skipping query (degraded mode)");
    rt.pendingRefetch = true;
    setStore({ loading: false });
    return;
  }

  rt.isFetching = true;

  try {
    const [roleResult, memberResult] = await Promise.all([
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle(),
      supabase
        .from("workspace_members")
        .select("workspace_id, workspaces!workspace_members_workspace_id_fkey(name, metadata), id, role, permissions, display_name")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const isAdmin = roleResult.data?.role === "admin" || roleResult.data?.role === "owner";

    // Track query results for circuit breaker
    trackQueryResult(roleResult.error);
    trackQueryResult(memberResult.error);

    if (memberResult.error) {
      console.error("[useWorkspace] Error checking membership:", memberResult.error);
      throw memberResult.error;
    }

    if (memberResult.data) {
      rt.retryCount = 0;
      stopBackgroundRetry();
      const row = memberResult.data as any;
      const perms = row?.permissions as Record<string, unknown> | null;
      setStore({
        workspaceId: row.workspace_id,
        workspaceName: (row.workspaces as any)?.name || "Meu Workspace",
        workspaceMetadata: ((row.workspaces as any)?.metadata as Record<string, unknown>) ?? null,
        loading: false,
        memberId: row.id ?? null,
        memberRole: row.role ?? null,
        memberDisplayName: row.display_name ?? null,
        memberPermissions: perms ?? null,
        invalidationCounter: 0,
      });
      return;
    }

    if (isAdmin) {
      rt.retryCount = 0;
      stopBackgroundRetry();
      await createWorkspaceForUser(userId, showToast);
      return;
    }

    // No workspace found and not admin — try check-subscription as fallback
    // to handle cases where Stripe sub exists but DB records are missing
    try {
      const { data: checkData, error: checkError } = await supabase.functions.invoke("check-subscription");
      if (checkError || !checkData?.subscribed) {
        rt.retryCount = 0;
        stopBackgroundRetry();
        setStore({ workspaceId: null, workspaceName: "", loading: false, ...initialMember, invalidationCounter: 0 });
        return;
      }
      console.log("[useWorkspace] Active Stripe subscription found, creating workspace...");
    } catch {
      rt.retryCount = 0;
      stopBackgroundRetry();
      setStore({ workspaceId: null, workspaceName: "", loading: false, ...initialMember, invalidationCounter: 0 });
      return;
    }

    rt.retryCount = 0;
    stopBackgroundRetry();
    await createWorkspaceForUser(userId, showToast);
  } catch (error: any) {
    console.error("[useWorkspace] Critical error:", error);

    if (isTransientError(error) && rt.retryCount < MAX_RETRIES) {
      rt.retryCount++;
      const delay = Math.min(2000 * Math.pow(2, rt.retryCount - 1), 30000);
      console.log(`[useWorkspace] Transient error, retrying in ${delay}ms (attempt ${rt.retryCount}/${MAX_RETRIES})`);
      rt.isFetching = false;
      if (rt.retryTimer) clearTimeout(rt.retryTimer);
      rt.retryTimer = setTimeout(() => {
        fetchWorkspace(userId, showToast);
      }, delay);
      return;
    }

    if (isTransientError(error)) {
      console.warn("[useWorkspace] All retries exhausted, starting background retry");
      rt.retryCount = 0;
      setStore({ loading: false });
      startBackgroundRetry(userId, showToast);
    } else {
      showToast({
        title: "Erro ao carregar workspace",
        description: error.message || "Não foi possível carregar o workspace.",
        variant: "destructive",
      });
      rt.retryCount = 0;
      setStore({ loading: false });
    }
  } finally {
    rt.isFetching = false;
  }
}

async function createWorkspaceForUser(userId: string, showToast: (t: any) => void) {
  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("id", userId)
    .maybeSingle();

  const name = profile?.full_name
    ? `Workspace de ${profile.full_name}`
    : "Meu Workspace";

  const { data: workspaceData, error: rpcError } = await supabase.rpc(
    "create_workspace_for_user",
    { p_user_id: userId, p_workspace_name: name }
  );

  if (rpcError) {
    console.error("[useWorkspace] Failed to create workspace via RPC:", rpcError);
    throw rpcError;
  }

  if (!workspaceData) {
    throw new Error("Failed to create workspace - no data returned");
  }

  const workspace = workspaceData as unknown as { id: string; name: string };
  setStore({
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    workspaceMetadata: null,
    loading: false,
    ...initialMember,
    invalidationCounter: 0,
  });

  // Fetch new member so store has full member info
  const { data: newMember } = await supabase
    .from("workspace_members")
    .select("id, role, permissions, display_name")
    .eq("user_id", userId)
    .eq("workspace_id", workspace.id)
    .maybeSingle();
  if (newMember) {
    const row = newMember as any;
    setStore({
      memberId: row.id ?? null,
      memberRole: row.role ?? null,
      memberDisplayName: row.display_name ?? null,
      memberPermissions: (row.permissions as Record<string, unknown>) ?? null,
      invalidationCounter: 0,
    });
  }

  showToast({
    title: "Workspace criado!",
    description: "Seu workspace foi configurado com sucesso.",
  });
}

/**
 * Invalida o cache do workspace/membro. Deve ser chamado quando role, permissions,
 * plano ou lista de membros mudar (ex.: após manage-workspace-members, billing).
 * O próximo uso de useWorkspace refetchará do banco.
 */
export function invalidateWorkspaceCache(): void {
  const rt = wsRuntime();
  rt.lastFetchedUserId = null;
  setStore({ invalidationCounter: (rt.store.invalidationCounter || 0) + 1 });
  notifyWsListeners();
}

/** Metadata do workspace atual (singleton store). Usado por feature flags per-workspace. */
export function getWorkspaceMetadata(): Record<string, unknown> | null {
  const store = wsRuntime().store;
  if (store.loading || !store.workspaceId) return null;
  return store.workspaceMetadata;
}

// ─── Hook ───────────────────────────────────────────────────────────

export const useWorkspace = (userId: string | undefined) => {
  const [, forceUpdate] = useState(0);
  const { toast } = useToast();
  const toastRef = useRef(toast);
  const rt = wsRuntime();
  const { store } = rt;

  useEffect(() => { toastRef.current = toast; }, [toast]);

  useEffect(() => {
    const listener = () => forceUpdate((c) => c + 1);
    rt.listeners.add(listener);
    return () => { rt.listeners.delete(listener); };
  }, [rt]);

  useEffect(() => {
    if (!userId) {
      if (rt.lastFetchedUserId !== null) {
        rt.lastFetchedUserId = null;
        Object.assign(rt.store, {
          workspaceId: null,
          workspaceName: "",
          workspaceMetadata: null,
          loading: false,
          ...initialMember,
          invalidationCounter: 0,
        });
        stopBackgroundRetry();
        notifyWsListeners();
      }
      setStore({ loading: false, ...initialMember, invalidationCounter: 0 });
      return;
    }

    // anterior antes de qualquer short-circuit. Sem isso, o singleton vazaria
    // dados de outro workspace entre trocas de conta (ex.: logout+login rápido).
    const userChanged = rt.lastFetchedUserId !== null && rt.lastFetchedUserId !== userId;
    if (userChanged) {
      Object.assign(rt.store, {
        workspaceId: null,
        workspaceName: "",
        workspaceMetadata: null,
        loading: true,
        ...initialMember,
        invalidationCounter: 0,
      });
      stopBackgroundRetry();
      notifyWsListeners();
    }

    if (userId === rt.lastFetchedUserId && !store.loading && (store.invalidationCounter || 0) === 0) {
      return;
    }

    rt.lastFetchedUserId = userId;
    rt.lastToastFn = toastRef.current;

    if (!userChanged && store.workspaceId && !store.loading && (store.invalidationCounter || 0) === 0) {
      return;
    }

    setStore({ loading: true, ...initialMember });
    fetchWorkspace(userId, toastRef.current);
  }, [userId, store.invalidationCounter]);


  const isAdmin = store.memberRole === "admin" || store.memberRole === "owner";
  const isOwner = store.memberRole === "owner";

  return {
    workspaceId: store.workspaceId,
    workspaceName: store.workspaceName,
    workspaceMetadata: store.workspaceMetadata,
    loading: store.loading,
    memberId: store.memberId,
    memberRole: store.memberRole,
    memberDisplayName: store.memberDisplayName,
    memberPermissions: store.memberPermissions,
    isAdmin,
    isOwner,
  };
};
